import { useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import { SaveStatus } from '@/types/common';

interface AutoSaveOptions<T> {
    delay?: number;
    onSave: (data: T) => Promise<void>;
    onError?: (error: any) => void;
    maxRetries?: number;
    onStatusChange?: (status: SaveStatus) => void;
}

export function useAutoSave<T>({ 
    delay = 1000, 
    onSave, 
    onError,
    maxRetries = 3,
    onStatusChange 
}: AutoSaveOptions<T>) {
    const pendingSave = useRef<T | null>(null);
    const retryCount = useRef(0);

    const saveWithRetry = async (data: T) => {
        try {
            onStatusChange?.({ status: 'saving' });
            await onSave(data);
            retryCount.current = 0;
            pendingSave.current = null;
            onStatusChange?.({ 
                status: 'saved', 
                lastSaved: new Date() 
            });
        } catch (error) {
            if (retryCount.current < maxRetries) {
                retryCount.current++;
                pendingSave.current = data;
                onStatusChange?.({ 
                    status: 'retrying', 
                    retryCount: retryCount.current 
                });
                setTimeout(() => {
                    if (pendingSave.current) {
                        saveWithRetry(pendingSave.current);
                    }
                }, Math.pow(2, retryCount.current) * 1000);
            } else {
                onError?.(error);
                onStatusChange?.({ 
                    status: 'error', 
                    error: error instanceof Error ? error.message : 'Save failed' 
                });
                pendingSave.current = null;
                retryCount.current = 0;
            }
        }
    };

    const debouncedSave = useCallback(
        debounce((data: T) => {
            saveWithRetry(data);
        }, delay),
        [delay]
    );

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [debouncedSave]);

    return debouncedSave;
}
